import React, { useState, useEffect, useRef, useCallback } from 'react'
import { toast } from 'react-toastify'
import { apiLabs, apiSantoLog } from '../../../Services/api'
import Dashboard from '../../../Components/Dashboard'
import { store } from '../../../store'
import history from '../../../Services/history'
import Denied from '../../../Components/Denied'
import {
  GlobalCarrierStyles, PageWrap, Topbar, Body, Toolbar, Search, Stats,
  NewBtn, RefreshBtn, ListWrap,
  CCard, CHead, Chevron, CName, CMeta, Actions, IconBtn,
  CBody, DriversHead, DriverRow, DName, DMono, DChecks, StatusBadge, EmptyDrivers,
  Empty, Loading,
  ModalOverlay, ModalBox, Label, Input, ModalActions, ModalBtn,
  ConfirmBox, ConfirmIcon, ConfirmTitle, ConfirmMessage, ConfirmActions, ConfirmBtn,
} from './style'

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function fmtDoc(doc) {
  const d = String(doc || '').replace(/\D/g, '')
  if (d.length === 11) return d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4')
  if (d.length === 14) return d.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5')
  return d
}

function apiToLocal(c) {
  const drivers = Array.isArray(c.drivers) ? c.drivers : []
  return {
    id: c.id,
    name: c.name || '',
    createdAt: c.createdAt || c.created_at || null,
    updatedAt: c.updatedAt || c.updated_at || null,
    drivers: drivers.map(d => ({
      id: d.id,
      name: d.name || '',
      document: d.document || '',
      vehicle_weight_capacity: Number(d.vehicle_weight_capacity ?? d.vehicleWeightCapacity ?? 0),
      order_quantity_capacity: Number(d.order_quantity_capacity ?? d.orderQuantityCapacity ?? 0),
      check_weight: Boolean(d.check_weight ?? d.checkWeight ?? false),
      check_order_quantity: Boolean(d.check_order_quantity ?? d.checkOrderQuantity ?? false),
      status: d.status !== false,
    })),
  }
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function SantoLogCarriers(props) {
  const { location: { idPage } } = props
  const [auth, setAuth] = useState(null)

  async function verifyAuth() {
    const { profile } = store.getState().user
    const res = await apiLabs.post('valid_key', { id: profile.id, menu_id: idPage })
    if (res.data.message === 'User not authorized') return setAuth('denied')
    if (res.data.message === 'User authorized') return setAuth('authorized')
    return history.push({ pathname: '/', state: { from: props.location } })
  }

  const [carriers, setCarriers] = useState([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(() => new Set()) // ids expandidos

  const [form, setForm] = useState({ open: false, id: null, name: '' })

  // Confirm modal genérico (Promise)
  const [confirmDialog, setConfirmDialog] = useState({
    open: false, title: '', message: '', confirmLabel: 'Confirmar', tone: 'warn',
  })
  const confirmResolveRef = useRef(null)
  function askConfirm({ title, message, confirmLabel = 'Confirmar', tone = 'warn' }) {
    return new Promise(resolve => {
      confirmResolveRef.current = resolve
      setConfirmDialog({ open: true, title, message, confirmLabel, tone })
    })
  }
  function closeConfirm(value) {
    setConfirmDialog(d => ({ ...d, open: false }))
    if (confirmResolveRef.current) {
      const r = confirmResolveRef.current
      confirmResolveRef.current = null
      r(Boolean(value))
    }
  }

  // ── INIT ──────────────────────────────────────────────────────────────────
  useEffect(() => { verifyAuth() }, []) // eslint-disable-line
  useEffect(() => {
    if (auth === 'authorized') fetchCarriers()
  }, [auth]) // eslint-disable-line

  async function fetchCarriers() {
    setLoading(true)
    try {
      const res = await apiSantoLog.get('/carrier')
      const list = Array.isArray(res.data) ? res.data
        : (res.data?.data || res.data?.carriers || [])
      setCarriers(list.map(apiToLocal))
    } catch (err) {
      console.error('GET /carrier failed:', err)
      toast.error('Erro ao carregar transportadoras: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setLoading(false)
    }
  }

  // ── EXPAND / COLLAPSE ─────────────────────────────────────────────────────
  const toggleExpand = useCallback((id) => {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  // ── FORM ──────────────────────────────────────────────────────────────────
  function openCreate() {
    setForm({ open: true, id: null, name: '' })
  }
  function openEdit(carrier) {
    setForm({ open: true, id: carrier.id, name: carrier.name || '' })
  }
  function closeForm() { setForm(f => ({ ...f, open: false })) }

  function validate() {
    if (!form.name.trim()) {
      toast.warn('Informe o nome da transportadora')
      return false
    }
    return true
  }

  async function saveCarrier() {
    if (!validate()) return
    const payload = { name: form.name.trim() }
    setSaving(true)
    try {
      if (form.id) {
        const res = await apiSantoLog.put(`/carrier/${form.id}`, payload)
        const saved = res.data?.data || res.data || {}
        setCarriers(prev => prev.map(c =>
          c.id === form.id
            ? { ...c, name: saved?.name ?? payload.name, updatedAt: saved?.updatedAt ?? new Date().toISOString() }
            : c
        ))
        toast.success('Transportadora atualizada com sucesso')
      } else {
        const res = await apiSantoLog.post('/carrier', payload)
        const saved = res.data?.data || res.data || {}
        const created = apiToLocal({
          id: saved?.id,
          name: saved?.name ?? payload.name,
          createdAt: saved?.createdAt ?? new Date().toISOString(),
          drivers: saved?.drivers || [],
        })
        setCarriers(prev => [created, ...prev])
        toast.success('Transportadora cadastrada com sucesso')
      }
      closeForm()
    } catch (err) {
      console.error('Save carrier failed:', err)
      toast.error('Erro ao salvar transportadora: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── DELETE ────────────────────────────────────────────────────────────────
  async function deleteCarrier(carrier) {
    const driversCount = carrier.drivers.length
    const warningSuffix = driversCount > 0
      ? `\n\n⚠ Esta transportadora possui ${driversCount} motorista${driversCount > 1 ? 's' : ''} vinculado${driversCount > 1 ? 's' : ''}.`
      : ''
    const ok = await askConfirm({
      title: 'Remover transportadora',
      message: `Tem certeza que deseja remover "${carrier.name}"? Esta ação não pode ser desfeita.${warningSuffix}`,
      confirmLabel: 'Remover',
      tone: 'danger',
    })
    if (!ok) return
    setSaving(true)
    try {
      await apiSantoLog.delete(`/carrier/${carrier.id}`)
      setCarriers(prev => prev.filter(c => c.id !== carrier.id))
      setExpanded(prev => {
        const next = new Set(prev)
        next.delete(carrier.id)
        return next
      })
      toast.success('Transportadora removida')
    } catch (err) {
      console.error('DELETE /carrier/:id failed:', err)
      toast.error('Erro ao remover transportadora: ' + (err?.response?.data?.error || err?.response?.data?.message || err.message))
    } finally {
      setSaving(false)
    }
  }

  // ── FILTER ────────────────────────────────────────────────────────────────
  const q = query.trim().toLowerCase()
  const filtered = !q ? carriers : carriers.filter(c => {
    const driversNames = c.drivers.map(d => d.name).join(' ').toLowerCase()
    return c.name.toLowerCase().includes(q) || driversNames.includes(q)
  })

  const totalDrivers = carriers.reduce((sum, c) => sum + c.drivers.length, 0)

  // ── RENDER ────────────────────────────────────────────────────────────────
  return (
    <Dashboard title="Transportadoras">
      {auth === 'denied' && <Denied />}
      {auth === 'authorized' && (
        <>
          <GlobalCarrierStyles />
          <PageWrap>
            <Topbar>
              <h2>🚚 Cadastro de Transportadoras</h2>
              <span className="crumb">Gerencie transportadoras e visualize seus motoristas vinculados</span>
            </Topbar>

            <Body>
              <Toolbar>
                <Search
                  placeholder="Buscar por nome da transportadora ou do motorista…"
                  value={query}
                  onChange={e => setQuery(e.target.value)}
                />
                <Stats>
                  <span><strong>{carriers.length}</strong>transportadoras</span>
                  <span><strong>{totalDrivers}</strong>motoristas no total</span>
                </Stats>
                <RefreshBtn onClick={fetchCarriers} disabled={loading}>
                  {loading ? '…' : '↻ Atualizar'}
                </RefreshBtn>
                <NewBtn onClick={openCreate} disabled={saving}>+ Nova transportadora</NewBtn>
              </Toolbar>

              <ListWrap>
                {loading && !carriers.length && <Loading>Carregando transportadoras…</Loading>}

                {!loading && !carriers.length && (
                  <Empty>
                    Nenhuma transportadora cadastrada.<br />
                    Clique em <strong>"Nova transportadora"</strong> para começar.
                  </Empty>
                )}

                {!loading && carriers.length > 0 && filtered.length === 0 && (
                  <Empty>Nenhuma transportadora corresponde à busca.</Empty>
                )}

                {filtered.map(carrier => {
                  const isOpen = expanded.has(carrier.id)
                  const dCount = carrier.drivers.length
                  const activeCount = carrier.drivers.filter(d => d.status).length
                  return (
                    <CCard key={carrier.id} $expanded={isOpen}>
                      <CHead onClick={() => toggleExpand(carrier.id)}>
                        <Chevron $open={isOpen}>▶</Chevron>
                        <CName>
                          {carrier.name}
                          {carrier.createdAt && (
                            <small>desde {new Date(carrier.createdAt).toLocaleDateString('pt-BR')}</small>
                          )}
                        </CName>
                        <CMeta>
                          <span><strong>{dCount}</strong>motorista{dCount !== 1 ? 's' : ''}</span>
                          {dCount > 0 && (
                            <span><strong>{activeCount}</strong>ativo{activeCount !== 1 ? 's' : ''}</span>
                          )}
                        </CMeta>
                        <Actions onClick={e => e.stopPropagation()}>
                          <IconBtn
                            onClick={() => openEdit(carrier)}
                            disabled={saving}
                            title="Editar transportadora"
                          >✏️</IconBtn>
                          <IconBtn
                            $danger
                            onClick={() => deleteCarrier(carrier)}
                            disabled={saving}
                            title="Remover transportadora"
                          >🗑</IconBtn>
                        </Actions>
                      </CHead>

                      {isOpen && (
                        <CBody>
                          {dCount === 0 ? (
                            <EmptyDrivers>Nenhum motorista vinculado a esta transportadora.</EmptyDrivers>
                          ) : (
                            <>
                              <DriversHead>
                                <span>Motoristas vinculados ({dCount})</span>
                              </DriversHead>
                              {carrier.drivers.map(d => (
                                <DriverRow key={d.id}>
                                  <DName>
                                    <span className="name">{d.name}</span>
                                    <span className="doc">{fmtDoc(d.document) || '—'}</span>
                                  </DName>
                                  <DMono>
                                    {d.vehicle_weight_capacity.toLocaleString('pt-BR')}
                                    <span className="unit">kg</span>
                                  </DMono>
                                  <DMono>
                                    {d.order_quantity_capacity}
                                    <span className="unit">peds</span>
                                  </DMono>
                                  <DChecks>
                                    <span className={d.check_weight ? 'on' : ''}>
                                      {d.check_weight ? '✓' : '—'} peso
                                    </span>
                                    <span className={d.check_order_quantity ? 'on' : ''}>
                                      {d.check_order_quantity ? '✓' : '—'} qtd
                                    </span>
                                  </DChecks>
                                  <StatusBadge $on={d.status}>
                                    {d.status ? 'Ativo' : 'Inativo'}
                                  </StatusBadge>
                                </DriverRow>
                              ))}
                            </>
                          )}
                        </CBody>
                      )}
                    </CCard>
                  )
                })}
              </ListWrap>
            </Body>
          </PageWrap>

          {/* MODAL CRIAR / EDITAR */}
          <ModalOverlay $visible={form.open}>
            <ModalBox>
              <h3>{form.id ? 'Editar transportadora' : 'Nova transportadora'}</h3>
              <p>
                {form.id
                  ? 'Altere o nome da transportadora.'
                  : 'Defina o nome da nova transportadora. Os motoristas podem ser vinculados depois pela tela de Motoristas.'}
              </p>

              <Label>Nome *</Label>
              <Input
                autoFocus
                value={form.name}
                onChange={e => {
                  const v = e.target.value
                  setForm(f => ({ ...f, name: v }))
                }}
                onKeyDown={e => {
                  if (e.key === 'Enter' && !saving) saveCarrier()
                  if (e.key === 'Escape') closeForm()
                }}
                placeholder="Ex: Alef Transportes"
                disabled={saving}
                maxLength={120}
              />

              <ModalActions>
                <ModalBtn onClick={closeForm} disabled={saving}>Cancelar</ModalBtn>
                <ModalBtn
                  $primary
                  onClick={saveCarrier}
                  disabled={saving || !form.name.trim()}
                >
                  {saving ? 'Salvando…' : (form.id ? 'Salvar alterações' : 'Cadastrar')}
                </ModalBtn>
              </ModalActions>
            </ModalBox>
          </ModalOverlay>

          {/* CONFIRM MODAL */}
          <ModalOverlay $visible={confirmDialog.open}>
            <ConfirmBox $tone={confirmDialog.tone}>
              <ConfirmIcon $tone={confirmDialog.tone}>
                {confirmDialog.tone === 'danger' ? '⚠' : '?'}
              </ConfirmIcon>
              <ConfirmTitle>{confirmDialog.title}</ConfirmTitle>
              <ConfirmMessage>{confirmDialog.message}</ConfirmMessage>
              <ConfirmActions>
                <ConfirmBtn onClick={() => closeConfirm(false)}>Cancelar</ConfirmBtn>
                <ConfirmBtn
                  $primary
                  $tone={confirmDialog.tone}
                  onClick={() => closeConfirm(true)}
                >
                  {confirmDialog.confirmLabel}
                </ConfirmBtn>
              </ConfirmActions>
            </ConfirmBox>
          </ModalOverlay>
        </>
      )}
    </Dashboard>
  )
}
